/**
 * Full client-state reset for the error boundary's "Reset & sign out" action.
 *
 * A plain `logout()` only ends the Auth0 session — it leaves the SPA SDK's
 * cached tokens (`@@auth0spajs@@…` in localStorage) and every bit of our own
 * localStorage/sessionStorage bookkeeping (login-redirect deep links,
 * impersonation, dismissed banners/tips, last selected view, etc.) in place.
 * When the broken state lives in one of those (e.g. a lock-contended/corrupt
 * token cache), a normal logout + re-login just reproduces the same failure
 * — historically, only changing the account's password (which forces Auth0
 * to fully reissue everything) has reliably unblocked affected users.
 *
 * This wipes all same-origin localStorage + sessionStorage before signing
 * out, reproducing that same clean-slate effect without a password change.
 * It's an intentional "reset everything" escape hatch offered only from the
 * fatal error boundary, so clearing unrelated preferences (dismissed
 * banners, in-progress drafts, etc.) alongside the auth state is an
 * acceptable trade-off.
 *
 * Does NOT call Auth0's `logout()` — callers should do that immediately
 * after, since `logout()` navigates away.
 */
export const clearAllClientStorage = () => {
  try {
    localStorage.clear();
  } catch {
    // localStorage may be unavailable (private browsing, storage quota) —
    // proceed to sessionStorage cleanup regardless.
  }

  try {
    sessionStorage.clear();
  } catch {
    // sessionStorage may be unavailable — nothing more we can do client-side.
  }
};
